import { useState, useCallback } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import type { FileSelectionPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'
import { buildFileSelectionPrecondition, buildFolderSelectionPrecondition } from '@/user_files/utils/preconditions'
import type { FileForSelection, FolderForSelection } from './useDriveSelection'

type UseMoveDialogParams = {
  files: FileForSelection[]
  folders: FolderForSelection[]
  /** The ID of the current folder. */
  currentFolderId: string | null
  /** An array containing the IDs of all selected selectable files. */
  selectedSelectableFileIds: string[]
  /** An array containing the IDs of all selected folders. */
  selectedFolderIds: string[]
}

type OpenMoveItemsArgs = {
  /** An array containing the IDs of all files to move. */
  fileIds?: string[]
  /** An array containing the IDs of all folders to move. */
  folderIds?: string[]
}

type UseMoveDialogMoveDialog = {
  /** Whether the move-destination UI is open. */
  open: boolean
  /** File IDs to move (snapshot taken when the dialog opens). */
  fileIds: string[]
  /** Folder IDs to move (snapshot taken when the dialog opens). */
  folderIds: string[]
  filePreconditions: FileSelectionPrecondition[]
  folderPreconditions: FolderSelectionPrecondition[]
}

type UseMoveDialogReturn = {
  /** Visibility and IDs to move; IDs are fixed at open time so confirm uses a stable snapshot. */
  moveDialog: UseMoveDialogMoveDialog
  /** Opens the dialog using the current drive selection. */
  openMoveForSelected: () => void
  /** Opens the dialog for explicit file and/or folder IDs. */
  openMoveItems: (args: OpenMoveItemsArgs) => void
  /** Closes the dialog and clears captured IDs and errors. */
  closeMoveDialog: () => void
  /** True while any user-files batched operation is in progress. */
  isSubmitting: boolean
}

export function useMoveDialog({ files, folders, currentFolderId, selectedSelectableFileIds, selectedFolderIds }: UseMoveDialogParams): UseMoveDialogReturn {
  const isAnyOperationActive = useAppSelector(selectIsUserFilesOperationActive)

  // Snapshot selection at open time so the confirmed move uses the rows the user selected.
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [fileIdsToMove, setFileIdsToMove] = useState<string[]>([])
  const [folderIdsToMove, setFolderIdsToMove] = useState<string[]>([])
  const [filePreconditions, setFilePreconditions] = useState<FileSelectionPrecondition[]>([])
  const [folderPreconditions, setFolderPreconditions] = useState<FolderSelectionPrecondition[]>([])

  const openMoveItems = useCallback(
    ({ fileIds = [], folderIds = [] }: OpenMoveItemsArgs) => {
      if (!currentFolderId) return
      if (fileIds.length === 0 && folderIds.length === 0) return
      setFileIdsToMove([...fileIds])
      setFolderIdsToMove([...folderIds])
      setFilePreconditions(files.filter((file) => fileIds.includes(file.id)).map(buildFileSelectionPrecondition))
      setFolderPreconditions(folders.filter((folder) => folderIds.includes(folder.id)).map(buildFolderSelectionPrecondition))
      setMoveDialogOpen(true)
    },
    [currentFolderId, files, folders]
  )

  const openMoveForSelected = useCallback(() => {
    openMoveItems({ fileIds: selectedSelectableFileIds, folderIds: selectedFolderIds })
  }, [openMoveItems, selectedSelectableFileIds, selectedFolderIds])

  const closeMoveDialog = useCallback(() => {
    setMoveDialogOpen(false)
    setFileIdsToMove([])
    setFolderIdsToMove([])
    setFilePreconditions([])
    setFolderPreconditions([])
  }, [])

  return {
    moveDialog: {
      open: moveDialogOpen,
      fileIds: fileIdsToMove,
      folderIds: folderIdsToMove,
      filePreconditions,
      folderPreconditions,
    },
    openMoveForSelected,
    openMoveItems,
    closeMoveDialog,
    isSubmitting: isAnyOperationActive,
  }
}
